import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { 
  format, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  addWeeks, 
  subWeeks, 
  isToday, 
  parseISO, 
  isValid,
  getHours 
} from 'date-fns';
import { CalendarPost } from './CalendarPost';
import { NoPostsCard } from '../common/NoPostsCard';
import type { Post } from '../../types/calendar';

interface WeekViewProps {
  currentDate: Date;
  onDateChange: (date: Date) => void;
  posts: Post[];
  onPostClick?: (post: Post) => void;
  onDrop?: (date: Date) => void;
  dragOverDate?: Date | null;
  onDragOver?: (date: Date | null) => void;
  onPostDragStart?: (e: React.DragEvent, post: Post) => void;
}

export const WeekView: React.FC<WeekViewProps> = ({
  currentDate,
  onDateChange,
  posts,
  onPostClick,
  onDrop,
  dragOverDate,
  onDragOver,
  onPostDragStart
}) => {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }); // Start on Monday
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  
  const days = eachDayOfInterval({ start: weekStart, end: weekEnd });
  
  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  
  // Generate hours from 0 AM to 11 PM (24 hours)
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  const getEffectiveDate = (post: Post): Date | null => {
    // Ideas use reminderAt as their calendar date
    if (post.status === 'idea') {
      const candidate = post.reminderAt || null;
      if (!candidate) return null;
      let d = parseISO(candidate);
      if (!isValid(d)) d = new Date(candidate);
      return isValid(d) ? d : null;
    }
    // Prefer publishedAt, fallback to scheduledAt
    const candidate = post.publishedAt || post.scheduledAt || null;
    if (!candidate) return null;
    let d = parseISO(candidate);
    if (!isValid(d)) d = new Date(candidate);
    return isValid(d) ? d : null;
  };

  const getPostsForDateAndHour = (date: Date, hour: number) => {
    const targetDateStr = format(date, 'yyyy-MM-dd');
    
    return posts.filter(post => {
      try {
        const effective = getEffectiveDate(post);
        if (!effective) return false;
        const postDateStr = format(effective, 'yyyy-MM-dd');
        const postHour = getHours(effective);
        return postDateStr === targetDateStr && postHour === hour;
      } catch (error) {
        console.error('Error processing post:', post.id, error);
        return false;
      }
    });
  };

  const handlePreviousWeek = () => {
    onDateChange(subWeeks(currentDate, 1));
  };

  const handleNextWeek = () => {
    onDateChange(addWeeks(currentDate, 1));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault(); // Allow drop
  };

  const handleDayDragOver = (e: React.DragEvent, date: Date, hour: number) => {
    e.preventDefault();
    if (onDragOver) {
      // Create a date with the specific hour for more precise hover
      const dateWithHour = new Date(date);
      dateWithHour.setHours(hour, 0, 0, 0);
      onDragOver(dateWithHour);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    // Only clear dragOver if we're leaving the day cell entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      if (onDragOver) {
        onDragOver(null);
      }
    }
  };

  const handleDayDrop = (e: React.DragEvent, date: Date, hour: number) => {
    e.preventDefault();
    if (onDragOver) {
      onDragOver(null);
    }
    if (onDrop) {
      // Create a date with the specific hour for drop
      const dateWithHour = new Date(date);
      dateWithHour.setHours(hour, 0, 0, 0);
      onDrop(dateWithHour);
    }
  };

  // Always show the week view grid, even when empty

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg overflow-hidden w-full max-w-full">
      <div className="overflow-x-auto">
        <div className="p-2 sm:p-4 flex items-center justify-between border-b border-gray-200">
          <button
            onClick={handlePreviousWeek}
            className="p-1 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          </button>
          <h2 className="text-lg sm:text-xl font-semibold">
            {format(weekStart, 'dd MMM')} - {format(weekEnd, 'dd MMM yyyy')}
          </h2>
          <button
            onClick={handleNextWeek}
            className="p-1 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          </button>
        </div>

        {/* Days header */}
        <div className="grid grid-cols-8 border-b border-gray-200 min-w-[400px]">
          <div className="p-1 sm:p-3 text-center text-xs sm:text-sm font-medium text-gray-700 border-r border-gray-100">
            {/* Empty space for hours column */}
          </div>
          {weekDays.map((day, index) => (
            <div
              key={day}
              className={`p-1 sm:p-3 text-center text-xs sm:text-sm font-medium border-r border-gray-100 last:border-r-0 ${
                isToday(days[index]) ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
              }`}
            >
              <div>{day}</div>
              <div className="text-sm sm:text-lg font-semibold mt-0.5 sm:mt-1">
                {format(days[index], 'd')}
              </div>
            </div>
          ))}
        </div>

        {/* Hours and content grid */}
        <div className="min-w-[400px]">
          {hours.map((hour) => (
            <div key={hour} className="grid grid-cols-8 border-b border-gray-100 min-h-12 sm:min-h-16">
              {/* Hour column */}
              <div className="p-1 sm:p-2 text-[10px] sm:text-xs text-gray-500 border-r border-gray-100 flex items-start justify-end pr-1 sm:pr-3">
                {hour}:00
              </div>
              
              {/* Day columns */}
              {days.map((day) => {
                const hourPosts = getPostsForDateAndHour(day, hour);
                const isCurrentDay = isToday(day);
                
                // More precise drag over detection that includes hour
                const isDragOver = dragOverDate && 
                  format(day, 'yyyy-MM-dd') === format(dragOverDate, 'yyyy-MM-dd') &&
                  dragOverDate.getHours() === hour;
                
                return (
                  <div
                    key={`${day.toString()}-${hour}`}
                    className={`p-0.5 sm:p-1 border-r border-gray-100 last:border-r-0 min-h-12 sm:min-h-16 transition-colors ${
                      isCurrentDay ? 'bg-primary-50/20' : 'bg-white hover:bg-gray-50'
                    } ${isDragOver ? 'bg-primary-100 ring-2 ring-primary-300' : ''}`}
                    onDragOver={(e) => handleDayDragOver(e, day, hour)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDayDrop(e, day, hour)}
                  >
                    {/* Posts for this hour */}
                    <div className="space-y-0.5 sm:space-y-1">
                      {hourPosts.map((post) => (
                        <div
                          key={post.id}
                          onClick={() => onPostClick?.(post)}
                          className="cursor-pointer"
                        >
                          <CalendarPost 
                            post={post} 
                            onDragStart={onPostDragStart}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}; 